/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { jsx, cssMap } from '@atlaskit/css';
import Skeleton from '@atlaskit/skeleton';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { token } from '@atlaskit/tokens';
import { defineMessage, useIntl } from '@atlassian/jira-intl';
import { EmbedHeaderSkeleton } from './EmbedHeaderSkeleton.tsx';

/**
 * Skeleton for the hubs embedded view.
 *
 * Mirrors the layout of HubsView:
 *   - EmbedHeaderSkeleton (back link + "Hubs" title)
 *   - Preview banner placeholder
 *   - Hub home content area with topic sections and article cards
 */

const loadingMessage = defineMessage({
	id: 'servicedesk-solution-composer.hubs-skeleton.loading',
	defaultMessage: 'Loading hubs',
	description: 'Aria label for the hubs skeleton',
});

const styles = cssMap({
	container: {
		display: 'flex',
		flexDirection: 'column',
		height: '100%',
		width: '100%',
		minWidth: '640px',
		boxSizing: 'border-box',
		backgroundColor: token('elevation.surface'),
	},
	content: {
		paddingInline: token('space.400'),
		flex: '1 1 auto',
		minHeight: '0px',
	},
	previewBanner: {
		backgroundColor: token('color.background.accent.gray.subtlest'),
		paddingBlock: token('space.100'),
		paddingInline: token('space.200'),
	},
	topicSection: {
		paddingBlockStart: token('space.400'),
	},
	cardGrid: {
		display: 'grid',
		gridTemplateColumns: 'repeat(2, 1fr)',
		gap: token('space.200'),
	},
	card: {
		backgroundColor: token('elevation.surface.raised'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		borderRadius: token('radius.large'),
		paddingTop: token('space.300'),
		paddingRight: token('space.300'),
		paddingBottom: token('space.300'),
		paddingLeft: token('space.300'),
	},
});

const ArticleCard = ({ titleWidth }: { titleWidth: number }) => (
	<Stack xcss={styles.card} space="space.150">
		<Skeleton
			width={titleWidth}
			height={token('space.200')}
			borderRadius={token('radius.xsmall')}
			isShimmering
		/>
		<Skeleton
			width="80%"
			height={token('space.150')}
			borderRadius={token('radius.xsmall')}
			isShimmering
		/>
	</Stack>
);

export const HubsSkeleton = (): JSX.Element => {
	const { formatMessage } = useIntl();

	return (
		<Box
			xcss={styles.container}
			role="status"
			aria-busy="true"
			aria-label={formatMessage(loadingMessage)}
		>
			<EmbedHeaderSkeleton />

			{/* Preview banner skeleton */}
			<Inline space="space.100" alignBlock="center" xcss={styles.previewBanner} aria-hidden="true">
				<Skeleton
					width={token('space.200')}
					height={token('space.200')}
					borderRadius={token('radius.full')}
					isShimmering
				/>
				<Skeleton
					width={240}
					height={token('space.200')}
					borderRadius={token('radius.xsmall')}
					isShimmering
				/>
			</Inline>

			<Box xcss={styles.content}>
				{/* Topic section 1 */}
				<Stack space="space.200" xcss={styles.topicSection} aria-hidden="true">
					<Skeleton
						width={160}
						height={token('space.300')}
						borderRadius={token('radius.xsmall')}
						isShimmering
					/>
					<div css={styles.cardGrid}>
						<ArticleCard titleWidth={180} />
						<ArticleCard titleWidth={140} />
						<ArticleCard titleWidth={200} />
						<ArticleCard titleWidth={160} />
					</div>
				</Stack>

				{/* Topic section 2 */}
				<Stack space="space.200" xcss={styles.topicSection} aria-hidden="true">
					<Skeleton
						width={120}
						height={token('space.300')}
						borderRadius={token('radius.xsmall')}
						isShimmering
					/>
					<div css={styles.cardGrid}>
						<ArticleCard titleWidth={200} />
						<ArticleCard titleWidth={150} />
					</div>
				</Stack>
			</Box>
		</Box>
	);
};
